import { IQpAuthenticationCredentials } from '@library/models/auth/qp-auth.models';
import { QpLoggerService } from '@library/services/qp-logger/qp-logger.service';
import { QpNotificationBarService } from '@library/services/qp-notification-bar/qp-notification-bar.service';
import { SERVER_API_URL } from '@one/app/app.constants';
import { CommonRoutes } from '@one/app/common-routes';
import { ConnectService } from '@one/app/pages/connect/connect.service';
import { TokenInformations } from '@one/app/shared/models/auth/auth-tokens.models';
import { AuthenticationService } from '@one/app/shared/services/auth/authentication.service';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { QimaOptionalType } from '@qima/ngx-qima';
import dayjs from 'dayjs';
import { Base64 } from 'js-base64';
import { JwtPayload, jwtDecode } from 'jwt-decode';
import { isNil } from 'lodash/index';
import { CookieService } from 'ngx-cookie';
import { SessionStorageService } from 'ngx-webstorage';
import { Observable, lastValueFrom, of, throwError } from 'rxjs';
import { catchError, finalize, map, switchMap, tap } from 'rxjs/operators';

@Injectable()
export class JwtAuthenticationService extends AuthenticationService {
  private readonly qimaConnectAccessTokenKey = 'connect.accessToken';
  private readonly tokenKey = 'authenticationToken';
  private readonly rootSessionTokenKey = 'rootSessionTokenKey';
  private readonly brdReturnUrlSessionKey = 'brdReturnUrlSessionKey';
  private readonly qimaConnectRefreshTokenKey = 'connect.refreshToken';
  private readonly qimaConnectClientIdKey = 'connect.clientId';

  public constructor(
    private readonly _httpClient: HttpClient,
    private readonly _cookieStorage: CookieService,
    private readonly _connectService: ConnectService,
    private readonly _router: Router,
    private readonly _sessionStorageService: SessionStorageService,
    private readonly _qpLoggerService: QpLoggerService,
    private readonly _qpNotificationBarService: QpNotificationBarService
  ) {
    super();
  }

  public getToken$(): Observable<QimaOptionalType<string>> {
    return of(this._getToken());
  }

  public login$(credentials: IQpAuthenticationCredentials): Observable<string | never> {
    this._cookieStorage.remove(this.tokenKey);
    this._sessionStorageService.clear(this.rootSessionTokenKey);

    return this._httpClient.post(`${SERVER_API_URL}api/authenticate`, credentials, { observe: 'response' }).pipe(
      map((resp): string => {
        const bearerToken = resp.headers.get('Authorization');

        if (bearerToken?.slice(0, 7) === 'Bearer ') {
          const jwt = bearerToken.slice(7, bearerToken.length);

          this._storeAuthenticationTokenOrThrow(jwt, credentials.rememberMe ?? false);

          return jwt;
        }

        throw new Error(`Unexpected format for the 'Authorization' headers`);
      })
    );
  }

  public loginAsBrand$(brandId: Readonly<number>): Observable<string> {
    return this._httpClient.post(`${SERVER_API_URL}api/authenticate/brand`, { brandId }, { observe: 'response' }).pipe(
      map((resp): string | never => {
        const jwt: string = this._extractBearerToken(resp.headers.get('Authorization'));

        this._storeAuthenticationTokenOrThrow(jwt, true);

        return jwt;
      })
    );
  }

  public clearBrandSelection(): Observable<void> {
    return this._httpClient.delete(`${SERVER_API_URL}api/authenticate/brand`, { observe: 'response' }).pipe(
      map((resp): void => {
        const jwt: string = this._extractBearerToken(resp.headers.get('Authorization'));

        this._storeAuthenticationTokenOrThrow(jwt, true);
      })
    );
  }

  public loginAsEntity$(entityId: Readonly<number>): Observable<string> {
    const jwtToken: string | undefined = this._getToken();

    if (!jwtToken) {
      return throwError((): string => 'No bearer token');
    }

    const brandId = this._getBrandId(jwtToken);

    return this._httpClient
      .post(
        `${SERVER_API_URL}api/authenticate/entity`,
        {
          brandId,
          entityId,
        },
        { observe: 'response' }
      )
      .pipe(
        map((resp): string | never => {
          const jwt: string = this._extractBearerToken(resp.headers.get('Authorization'));

          this._storeAuthenticationTokenOrThrow(jwt, true);

          return jwt;
        })
      );
  }

  public loginAsInspector$(inspectorId: Readonly<number>): Observable<string> {
    const jwtToken: string | undefined = this._getToken();

    if (!jwtToken) {
      return throwError((): string => 'No bearer token');
    }

    // Store the current token in the session to be able to log back as supervisor after that
    this._copyAuthenticationTokenToRootSession();

    const brandId = this._getBrandId(jwtToken);

    return this._httpClient
      .post(
        `${SERVER_API_URL}api/authenticate/inspector`,
        {
          brandId,
          inspectorId,
        },
        { observe: 'response' }
      )
      .pipe(
        map((resp): string | never => {
          const jwt: string = this._extractBearerToken(resp.headers.get('Authorization'));

          this._storeAuthenticationTokenOrThrow(jwt, true);

          return jwt;
        })
      );
  }

  public storeAuthenticationToken(jwt, rememberMe): boolean {
    if (rememberMe) {
      this._cookieStorage.put(this.tokenKey, jwt, {
        expires: dayjs().add(30, 'days').toDate(),
      });
    } else {
      this._cookieStorage.put(this.tokenKey, jwt);
    }

    return this._checkAuthenticationTokenStored(jwt);
  }

  public removeAuthenticationToken(): void {
    this._cookieStorage.remove(this.tokenKey);
  }

  public logBackWithRootSessionToken(): void {
    const jwt = this._getRootSessionToken();

    if (!jwt) {
      return;
    }

    this._cookieStorage.put(this.tokenKey, jwt);
    this._sessionStorageService.clear(this.rootSessionTokenKey);
  }

  public storeBrdReturnUrlBeforeFieldMode(returnUrl: string): void {
    this._sessionStorageService.store(this.brdReturnUrlSessionKey, returnUrl);
  }

  public getBrdReturnUrlAndClear(): QimaOptionalType<string> {
    const returnUrl = this._sessionStorageService.retrieve(this.brdReturnUrlSessionKey);

    this._sessionStorageService.clear(this.brdReturnUrlSessionKey);

    return returnUrl;
  }

  public hasRootSessionToken(): boolean {
    return !isNil(this._getRootSessionToken());
  }

  public hasQimaConnectSession(): boolean {
    return (
      this._cookieStorage.hasKey(this.qimaConnectAccessTokenKey) &&
      this._cookieStorage.hasKey(this.qimaConnectRefreshTokenKey) &&
      this._cookieStorage.hasKey(this.qimaConnectClientIdKey)
    );
  }

  public storeQimaConnectTokens(accessToken: string, refreshToken: string, clientId: string): void {
    this._cookieStorage.put(this.qimaConnectAccessTokenKey, accessToken);
    this._cookieStorage.put(this.qimaConnectRefreshTokenKey, refreshToken);
    this._cookieStorage.put(this.qimaConnectClientIdKey, clientId);
  }

  public async isAuthenticated(): Promise<boolean> {
    const jwtToken = this._getToken();

    if (jwtToken) {
      const expirationDate = this._getExpirationTime(jwtToken);

      if (Date.now() / 1000 < expirationDate) {
        return Promise.resolve(true);
      }
    }

    return Promise.resolve(false);
  }

  public clearExpiredSession(): void {
    const jwtToken = this._getToken();

    if (jwtToken && Date.now() / 1000 >= this._getExpirationTime(jwtToken)) {
      this.removeAuthenticationToken();
    }
  }

  public async logout(): Promise<void> {
    this._cookieStorage.remove(this.tokenKey);
    this._sessionStorageService.clear(this.rootSessionTokenKey);

    return lastValueFrom(
      this._logoutFromQimaConnect$().pipe(
        finalize((): void => {
          this.redirectToLogin();
        })
      )
    );
  }

  public redirectToLogin(): void {
    this._connectService.redirectToConnectOrNothing();
    void this._router.navigate([CommonRoutes.login()]);
  }

  public handleUnauthorized(_error: HttpErrorResponse): void {
    this.removeAuthenticationToken();
    this.redirectToLogin();
  }

  public async setupAuthentication(): Promise<void> {
    return Promise.resolve();
  }

  public getQimaConnectToken$(): Observable<string> {
    const accessToken = this._cookieStorage.get(this.qimaConnectAccessTokenKey);

    if (accessToken && this._isQimaConnectSessionExpired()) {
      return this._connectService
        .refreshToken$(
          this._cookieStorage.get(this.qimaConnectClientIdKey) ?? '',
          jwtDecode(accessToken).sub ?? '',
          this._cookieStorage.get(this.qimaConnectRefreshTokenKey) ?? ''
        )
        .pipe(
          tap((refreshedAccessToken): void => this._cookieStorage.put(this.qimaConnectAccessTokenKey, refreshedAccessToken)),
          catchError((error: unknown): Observable<string> => {
            if (error instanceof HttpErrorResponse && error.status === 401) {
              this.revokeQimaConnectSession();
              this.redirectToLogin();
            }

            return of('');
          })
        );
    }

    return of(accessToken ?? '');
  }

  public revokeQimaConnectSession(): void {
    this.removeAuthenticationToken();
    this._cookieStorage.remove(this.qimaConnectAccessTokenKey);
    this._cookieStorage.remove(this.qimaConnectRefreshTokenKey);
    this._cookieStorage.remove(this.qimaConnectClientIdKey);
  }

  private _getExpirationTime(token: string): number {
    const payloadJson = this._decodeToken(token);

    return payloadJson.exp;
  }

  private _getBrandId(token: string): number {
    const payloadJson = this._decodeToken(token);

    return payloadJson.brandId;
  }

  private _extractBearerToken(bearerToken: QimaOptionalType<string>): string {
    if (bearerToken?.slice(0, 7) === 'Bearer ') {
      return bearerToken.slice(7, bearerToken.length);
    }

    throw new Error('No bearer token');
  }

  private _decodeToken(token: string): TokenInformations {
    const payload: string = Base64.decode(token.split('.')[1]);

    return JSON.parse(payload);
  }

  private _getToken(): string | undefined {
    return this._cookieStorage.get(this.tokenKey);
  }

  /**
   * Store the token for the login flows. When the browser refuses the cookie, the error is raised so
   * that the calling flow stops (no identity load, no navigation) and the user stays on the current page,
   * where the notification is displayed (PL-31280).
   */
  private _storeAuthenticationTokenOrThrow(jwt: string, rememberMe: boolean): void {
    if (!this.storeAuthenticationToken(jwt, rememberMe)) {
      throw new Error('Authentication cookie not stored');
    }
  }

  /**
   * The browser silently drops a cookie whose name + value exceed 4096 bytes (PL-31280).
   * Read the cookie back so that this case shows up in the logs instead of being silent.
   * The token itself is never logged, only its length.
   *
   * @returns true when the cookie holds the token, false otherwise
   */
  private _checkAuthenticationTokenStored(jwt: string): boolean {
    if (this._cookieStorage.get(this.tokenKey) === jwt) {
      return true;
    }

    this._qpLoggerService.error(
      '[JwtAuthenticationService] authentication cookie was not stored, the token may exceed the browser cookie size limit',
      { tokenLength: jwt?.length }
    );
    this._qpNotificationBarService.error('connect.session.token-not-stored');

    return false;
  }

  private _getRootSessionToken(): string | undefined {
    return this._sessionStorageService.retrieve(this.rootSessionTokenKey);
  }

  private _copyAuthenticationTokenToRootSession(): string | void {
    const jwt = this._getToken();

    if (!jwt) {
      return;
    }

    this._sessionStorageService.store(this.rootSessionTokenKey, jwt);

    return jwt;
  }

  private _isQimaConnectSessionExpired(): boolean {
    const accessToken = this._cookieStorage.get(this.qimaConnectAccessTokenKey) ?? '';
    const accessTokenPayload = jwtDecode(accessToken);

    return !isNil(accessTokenPayload.exp) && accessTokenPayload.exp * 1000 <= Date.now();
  }

  private _logoutFromQimaConnect$(): Observable<void> {
    if (this.hasQimaConnectSession()) {
      const accessToken = this._cookieStorage.get(this.qimaConnectAccessTokenKey) ?? '';
      const accessTokenPayload = jwtDecode<JwtPayload & { username: string }>(accessToken);
      let logoutCall$: Observable<void>;

      if (this._isQimaConnectSessionExpired()) {
        logoutCall$ = this._connectService
          .refreshToken$(
            this._cookieStorage.get(this.qimaConnectClientIdKey) ?? '',
            accessTokenPayload.sub ?? '',
            this._cookieStorage.get(this.qimaConnectRefreshTokenKey) ?? ''
          )
          .pipe(
            tap((refreshedAccessToken): void => this._cookieStorage.put(this.qimaConnectAccessTokenKey, refreshedAccessToken)),
            switchMap(
              (refreshedAccessToken): Observable<void> =>
                this._connectService.logout$(refreshedAccessToken, accessTokenPayload.username ?? '')
            ),
            catchError((error: unknown): Observable<void> => {
              this._qpLoggerService.error('[JwtAuthenticationService] QIMA Connect logout refresh failed', error);

              return of(undefined);
            })
          );
      } else {
        logoutCall$ = this._connectService.logout$(accessToken ?? '', accessTokenPayload.username ?? '');
      }

      return logoutCall$.pipe(tap((): void => this.revokeQimaConnectSession()));
    }

    return of(undefined);
  }
}
